import { CSSProperties, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { I18nWording, Locale, SUPPORTED_LOCALES } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';

import InputText, { InputTextProps } from './InputText';

import './TranslatableInputText.css';

const { block, element } = createBEMClasses('translatable-input-text');

type LocaleOption = {
  id: Locale;
  text: string;
};

type TranslatableInputTextProps = Omit<InputTextProps, 'size' | 'onChange' | 'type' | 'value'> & {
  size?: 'small' | 'medium' | 'large';
  value: I18nWording;
  onChange: (value: I18nWording) => void;
  wrapperStyle?: CSSProperties;
};

export const TranslatableInputText = ({
  value,
  onChange,
  size = 'medium',
  isDisabled,
  isInvalid,
  className,
  wrapperStyle,
  ...inputTextProps
}: TranslatableInputTextProps) => {
  const { i18n, t } = useLingui();
  const [selectedLocale, setSelectedLocale] = useState<Locale>(i18n.locale as Locale);

  const localeOptions = useMemo<LocaleOption[]>(
    () =>
      SUPPORTED_LOCALES.map((locale) => ({
        id: locale.id,
        text: locale.id.toUpperCase(),
      })),
    []
  );

  return (
    <div className={block(undefined, className)} style={wrapperStyle}>
      <div className={element('locale')}>
        <InputSelect<LocaleOption, false>
          aria-label={t({
            id: 'components.input-locale.label',
            message: 'Language',
          })}
          size={size}
          variant="select"
          options={localeOptions}
          value={localeOptions.find((option) => option.id === selectedLocale)}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.text}
          onChange={(option) => {
            if (option) {
              setSelectedLocale(option.id);
            }
          }}
          isSearchable={false}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
        />
      </div>

      <div className={element('input')}>
        <InputText
          {...inputTextProps}
          size={size === 'small' ? 'x-small' : size}
          isDisabled={isDisabled}
          value={value[selectedLocale] ?? ''}
          onChange={(nextValue) => {
            onChange({
              ...value,
              [selectedLocale]: nextValue,
            });
          }}
          isInvalid={isInvalid}
        />
      </div>
    </div>
  );
};
